import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, StatusChip, StatusTone } from '@/components/core';
import { formatDate } from '@/lib/utils/custom-date/custom-date';
import { ActivityLog, ActivityLogAction } from '../../types/activity-log.types';

const ACTION_TONE: Record<ActivityLogAction, StatusTone> = {
  Insert: 'success',
  Update: 'info',
  Delete: 'error',
};

/**
 * Curated list of module ids that write to ActivityLog. Mirrors the `id`s
 * used in `src/constant/hr-sidebar-menu.ts` -- keep in sync as more modules
 * adopt `logActivity()` (src/lib/activity-log.ts).
 */
const MODULE_OPTIONS = [
  'hr-department-manager',
  'hr-designation-manager',
  'hr-location-manager',
  'hr-project-manager',
  'hr-kpi-configuration',
  'hr-directory',
  'hr-my-attendance',
  'hr-manage-attendance',
  'hr-leave-builder',
  'hr-holiday',
  'hr-my-leave',
  'hr-leave-calendar',
  'hr-leave-manager',
  'hr-sick-leave',
  'hr-my-toil',
  'hr-toil-manager',
  'hr-overtime-manager',
  'hr-form-creation',
  'hr-reviews',
  'hr-my-submissions',
  'hr-peer-requests',
  'hr-request-lunch',
  'hr-manage-lunch',
  'hr-potential-profiles',
  'hr-conditional-offers',
  'hr-survey-form-creation',
  'hr-surveys',
  'hr-survey-my-submissions',
  'hr-service-desk',
  'hr-performance-dashboard',
];

const ACTION_OPTIONS: ActivityLogAction[] = ['Insert', 'Update', 'Delete'];

interface CreateActivityLogTableColumnsProps {
  t: (key: string) => string;
}

export const createActivityLogTableColumns = ({
  t,
}: CreateActivityLogTableColumnsProps): ColumnDef<ActivityLog>[] => [
  {
    id: 'CreatedDate',
    // Card: the title, with the action chip on its right.
    meta: { mobile: 'title', filter: { type: 'datetime' } },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('LOGS_COLUMN_TIMESTAMP')} />
    ),
    accessorFn: (row) => row.CreatedDate ?? '',
    cell: ({ row }) =>
      row.original.CreatedDate ? formatDate(new Date(row.original.CreatedDate)) : '-',
  },
  {
    id: 'PerformedByName',
    // Card: its own line, above Module | Entity.
    meta: { mobile: 'full', filter: { type: 'text' } },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('LOGS_COLUMN_PERFORMED_BY')} />
    ),
    accessorFn: (row) => row.PerformedByName || row.PerformedBy || '',
    cell: ({ row }) => (
      // Card: wrapped in full; an id (no name stored) has no spaces to wrap at.
      <span className="truncate max-md:whitespace-normal max-md:break-all">
        {row.original.PerformedByName || row.original.PerformedBy || '-'}
      </span>
    ),
  },
  {
    id: 'ModuleName',
    meta: {
      filter: {
        type: 'options',
        dropdown: true,
        options: MODULE_OPTIONS.map((module) => ({ value: module, label: module })),
      },
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('LOGS_COLUMN_MODULE')} />
    ),
    accessorFn: (row) => row.ModuleName ?? '',
  },
  {
    id: 'EntityType',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('LOGS_COLUMN_ENTITY')} />
    ),
    accessorFn: (row) => row.EntityType ?? '',
  },
  {
    id: 'Action',
    // Card: top right.
    meta: {
      mobile: 'badge',
      filter: {
        type: 'options',
        options: ACTION_OPTIONS.map((action) => ({
          value: action,
          label: t(action.toUpperCase()),
        })),
      },
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('LOGS_COLUMN_ACTION')} />
    ),
    accessorFn: (row) => row.Action ?? '',
    cell: ({ row }) => (
      <StatusChip tone={ACTION_TONE[row.original.Action as ActivityLogAction] ?? 'muted'}>
        {t(String(row.original.Action ?? '').toUpperCase())}
      </StatusChip>
    ),
  },
  {
    id: 'Summary',
    // Card: in the footer, wrapped rather than cut off.
    meta: { filter: { type: 'text' }, mobile: 'footer' },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('LOGS_COLUMN_SUMMARY')} />
    ),
    accessorFn: (row) => row.Summary ?? '',
    cell: ({ row }) => (
      <span className="truncate max-md:whitespace-normal">{row.original.Summary || '-'}</span>
    ),
  },
];
